import PIXIRenderer from '../../Render/PIXIRenderer';
import BaseUISystem from '../BaseUISystem';
import Interactable from 'Component/Interactable';
import { GetAnimationClip, PlayAnimation, StartAnimation } from '../../Animation/AnimationHelper';
import { RenderSystemEvent } from '../../Render/RenderSystem';
import { PlaySound } from '../../Audio/AudioHelper';
import BaseUIHandler from '../BaseUIHandler';
import GameObject from 'GameObject/GameObject';
import Renderable from 'Component/Renderable';
import Element from 'Prototype/UI/Element';
import { captureException } from '@sentry/core';
import { IGameState } from 'State/Game/IGameState';
import { IUIState } from 'State/UI/IUIState';
import { PopupID } from 'State/UI/PopupID';
import { UIActions } from 'State/UI/UIActions';
import { report } from 'report';

export enum MenuState {
    OPEN = 'open',
    CLOSED = 'closed',
}

export default class BaseMenu extends BaseUIHandler {
    public clipShow: string;
    public clipHide: string;

    public audioClipShow: string;
    public audioClipHide: string;

    public popupOverlayEnabled: boolean;

    public currentState: MenuState;

    /**
     * Elements to be enabled/disabled during animations.
     */
    private interactables: Interactable[];

    constructor(name: PopupID, system: BaseUISystem, hasDataFile: boolean = true) {
        super(name, system, hasDataFile);
        this.clipShow = 'MenuShow';
        this.clipHide = 'MenuHide';
        this.audioClipShow = 'swoosh-1';
        this.audioClipHide = 'swoosh-2';
        this.popupOverlayEnabled = true;
        this.currentState = MenuState.CLOSED;
        this.interactables = [];
    }

    public create() {
        if (this.active) return;
        this.createElement();
        this.interactables = this.element.getComponentsInChildren(Interactable);
        this.onCreate();

        try {
            this.element.container.resize(PIXIRenderer.renderer.width, PIXIRenderer.renderer.height);
        } catch (e) {
            console.log('Error resizing element', e);
        }

        this.active = true;
    }

    // tslint:disable-next-line: no-empty
    public handleStateChanged(state: IGameState): void {
        super.handleStateChanged(state);
    }

    public *show(state: IUIState): IterableIterator<number> {
        try {
            if (!this.active) return;

            this.system.popupLayer.addChild(this.element);

            if (this.popupOverlayEnabled) {
                // Bring overlay and popup to front.
                this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.system.popupLayer);

                this.system.scene.emit(RenderSystemEvent.BRING_TO_FRONT, this.system.UIOverlay);
            }

            this.toggleInteractables(false);
            this.onShow(state.params);

            if (this.popupOverlayEnabled) {
                StartAnimation(this.system.overlayPopup, 'OverlayFadeIn');
            }

            PlaySound(this.audioClipShow);

            yield* PlayAnimation(GetAnimationClip(this.clipShow, this.element));

            this.toggleInteractables(true);
            this.onShown(state.params);
        } catch (e) {
            console.warn(`${this.name} could not be opened`, e);
            report('Failed to open popup', {
                error: e,
                once: true,
                sampleRate: 0.1,
                tags: {
                    popupID: this.name,
                },
                fingerprint: 'ShowPopup:FailedToShow',
            });
            UIActions.hidePopup(this.name);
        }
    }

    public *hide(state: IUIState): IterableIterator<number> {
        this.toggleInteractables(false);
        this.onHide(state.params);

        if (this.popupOverlayEnabled) {
            StartAnimation(this.system.overlayPopup, 'OverlayFadeOut');
        }

        PlaySound(this.audioClipHide);

        yield* PlayAnimation(GetAnimationClip(this.clipHide, this.element));

        this.onHidden(state.params);

        if (!this.alwaysVisible) {
            this.destroy();
        }
    }

    protected toggleInteractables(value: boolean): void {
        const interactables: Interactable[] = this.element.getComponentsInChildren(Interactable);
        interactables.forEach(interactable => {
            interactable.enabled = value;
        });
    }

    protected restoreInteractivity(): void {
        const interactables: Interactable[] = this.element.getComponentsInChildren(Interactable);
        interactables.forEach(interactable => {
            interactable.enabled = interactable.previouslyEnabled;
        });
    }

    protected createElement() {
        //if (!this.UIData) return;
        this.element = GameObject.Instantiate(Element, this.getElementInitializeData());

        this.element.getComponent(Renderable).visible = this.alwaysVisible;

        this.system.scene.addGameObject(this.element);
    }
    protected getElementInitializeData() {
        return this.UIData;
    }

    public onDestroy(): void {
        if (this.element) {
            this.element.children.forEach(child => child.destroy());
            //this.element.destroy();
        }
    }
}
